import { useState } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import axios from '/components/configs/axios'
import styles from './styles.module.scss'
import Button from '../../../elements/Button'
import Spinner from '../../../elements/Spinner'
import { useRouter } from 'next/router'

const CheckoutForm = ({
  total,
  propertyId,
  listingId,
  startDate,
  endDate,
  bedrooms,
  email,
  firstName,
  lastName,
  setPlans,
  guests,
  setReservationId,
  propertyName,
  coupon,
  setSkip,
  bonuses,
  freeNights,
  freeNightlyRate,
  initialPrice,
  translations,
  manually_blocked = false,
  status = 'pending'
}) => {
  const [message, setMessage] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentDone, setPaymentDone] = useState(false)

  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()

  if (!stripe || !elements) {
    return
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    axios
      .post('reservation/price-calculation', {
        lang: router.locale,
        id: propertyId,
        total: total * 100,
        startDate: startDate,
        endDate: endDate,
        bedrooms: bedrooms
      })
      .then(async () => {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement)
        })

        if (!error) {
          try {
            const { id } = paymentMethod
            axios
              .post('reservation/payment-intent', {
                amount: total * 100,
                firstName: firstName,
                lastName: lastName,
                email: email
              })
              .then((response) => {
                if (response.status === 200) {
                  const clientKey = response.data[0]
                  const paymentIntentId = response.data[1]
                  const customerId = response.data[2]
                  stripe
                    // eslint-disable-next-line no-undef
                    .confirmCardPayment(clientKey, {
                      payment_method: {
                        card: elements.getElement(CardElement),
                        billing_details: {
                          name: firstName + ' ' + lastName
                        }
                      }
                    })
                    .then((response) => {
                      if (response.paymentIntent) {
                        axios
                          .post('reservation/guesty', {
                            firstName: firstName,
                            lastName: lastName,
                            email: email,
                            startDate: startDate,
                            endDate: endDate,
                            propertyId: propertyId,
                            listingId: listingId,
                            amount: total * 100,
                            paymentIntentId: paymentIntentId,
                            customerId: customerId,
                            coupon,
                            id,
                            guests,
                            bonuses,
                            freeNights,
                            freeNightlyRate,
                            initialPrice,
                            manually_blocked
                          })
                          .then((response) => {
                            // Confirm payment email notification
                            axios
                              .post('email/reservation', {
                                total,
                                propertyName,
                                startDate,
                                endDate,
                                bedrooms,
                                email,
                                firstName,
                                lastName,
                                guests,
                                coupon,
                                bonuses,
                                freeNights,
                                freeNightlyRate,
                                initialPrice
                              })
                              .then(() => {
                                setIsLoading(false)
                                setMessage(
                                  translations?.paymentSucceeded[router.locale]
                                )
                                setPaymentDone(true)
                                setReservationId(response.data.reservationId)
                                setTimeout(() => {
                                  setMessage(null)
                                  setPlans('thankYou')
                                }, 2000)
                              })
                          })
                      } else {
                        setIsLoading(false)
                        setErrorMessage(response.error.message)
                        // Payment failed email notification
                        axios.post('email/payment', {
                          total,
                          propertyName,
                          startDate,
                          endDate,
                          bedrooms,
                          email,
                          firstName,
                          lastName,
                          guests,
                          coupon,
                          bonuses,
                          freeNights,
                          freeNightlyRate,
                          initialPrice
                        })
                        setTimeout(() => {
                          setErrorMessage(null)
                        }, 8000)
                      }
                    })
                }
              })
          } catch (error) {
            setIsLoading(false)
          }
        } else {
          setIsLoading(false)
          setErrorMessage(error.message)
        }
      })
      .catch(() => {
        setIsLoading(false)
        setMessage(translations?.error[router.locale])
      })
  }

  return (
    <div>
      <form className={styles.payment_form} onSubmit={handleSubmit}>
        <CardElement />

        <Button
          variant={['color-accent', 'full-width']}
          arrow={!isLoading}
          disabled={isLoading || !stripe || !elements || paymentDone}
        >
          <span id="button-text">
            {isLoading ? (
              <div className={styles.label}>
                <Spinner />
                {translations?.confirming[router.locale]}
              </div>
            ) : (
              translations?.confirm[router.locale]
            )}
          </span>
        </Button>
        {message && <div className={styles.payment_message}>{message}</div>}
        {errorMessage && (
          <div className={styles.payment_error_message}>{errorMessage}</div>
        )}

        <div
          dangerouslySetInnerHTML={{
            __html: translations?.note[router.locale]
          }}
          className={styles.note}
        ></div>
      </form>

      <div className={styles.skip}>
        <Button
          variant={['label', 'xs']}
          onClick={() =>
            // Skip payment email notification
            axios
              .post('email/skip-payment', {
                propertyId: propertyId,
                listingId: listingId,
                total,
                propertyName,
                startDate,
                endDate,
                bedrooms,
                email,
                firstName,
                lastName,
                guests,
                coupon,
                bonuses,
                freeNights,
                freeNightlyRate,
                initialPrice,
                manually_blocked,
                status
              })
              .then(() => {
                setMessage(null)
                setPlans('finish')
                setSkip(true)
              })
          }
        >
          {translations?.skip[router.locale]}
        </Button>

        <p>{translations?.skipNote[router.locale]}</p>
      </div>
    </div>
  )
}

export default CheckoutForm
